"use client";

import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin } from "lucide-react";
import AddressForm from "./AddressForm";
import EditAddressForm from "./EditAddressForm";
import axios from "axios";
import Swal from "sweetalert2";
import PayUForm from "../paymentpage/PayUForm";
import { useMenu } from "../../context/MenuContext";
import GuestAddressForm from "./GuestAddressForm";

const PaymentPage = () => {
  const [addresses, setAddresses] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [cart, setCart] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [payUData, setPayUData] = useState(null);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [showCODModal, setShowCODModal] = useState(false);
  const userId = localStorage.getItem("authUserId");
  const authToken =
    localStorage.getItem("authToken") || localStorage.getItem("guest_token");

  const {
    promoCode,
    promoDiscount,
    finalAmount,
    fetchMenuCounts,
    setCartCount,
    shippingCharge,
    fetchShippingCharge,
  } = useMenu();
  const drawerRef = useRef(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    line1: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    contact: "",
    landmark: "",
    addressType: "",
  });

  // useEffect(() => {
  //   fetchAddresses();
  //   fetchCart();
  // }, []);

  const fetchAddresses = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      const userId = localStorage.getItem("authUserId"); // assuming you store userId in localStorage

      if (userId && authToken) {
        // Fetch from API if logged in
        const res = await axios.get(
          "https://tyka.premierhostings.com/backend/api/user-addresses",
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        const data = res.data?.data;
        console.log("data", data);

        if (Array.isArray(data)) {
          setAddresses(data);
          if (data.length > 0) setSelectedAddress(data[0].id);
        } else if (typeof data === "object" && data !== null) {
          setAddresses([data]);
          setSelectedAddress(data.id);
        } else {
          setAddresses([]);
        }
      } else {
        // If no userId/authToken, get guest address from localStorage
        const guestAddressString = localStorage.getItem("guestAddress");
        console.log("guestAddressString", guestAddressString);
        if (guestAddressString) {
          const guestAddress = JSON.parse(guestAddressString);
          // Wrap guestAddress in array to keep consistent format
          setAddresses([guestAddress]);
          // You might want to set selectedAddress to something, maybe an ID or just leave it null
          setSelectedAddress(null);
        } else {
          setAddresses([]);
        }
      }
    } catch (err) {
      console.error("❌ Error fetching addresses:", err);
      setAddresses([]);
    }
  };

  const fetchCart = async () => {
    setIsCartLoading(true);
    try {
      const authToken =
        localStorage.getItem("authToken") ||
        localStorage.getItem("guest_token");

      let res;
      if (authToken && userId) {
        res = await axios.get(
          "https://tyka.premierhostings.com/backend/api/cart",
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
      } else {
        res = await axios.get(
          `https://tyka.premierhostings.com/backend/api/cart?guest_token=${authToken}`
        );
      }

      const data = res.data?.data || [];

      if (Array.isArray(data) && data.length > 0) {
        const allItems = data.flatMap((cart) => cart.items || []);
        if (allItems.length > 0) {
          setCart(allItems);
          setCartCount(allItems.length);
          const totalPrice = allItems.reduce(
            (acc, item) => acc + Number.parseFloat(item.total || 0),
            0
          );
          setTotal(totalPrice);
        } else {
          setCart([]);
          setCartCount(0);
          setTotal(0);
        }
      } else {
        setCart([]);
        setCartCount(0);
        setTotal(0);
      }
    } catch (err) {
      console.error("❌ Error fetching cart:", err);
      setCart([]);
      setCartCount(0);
      setTotal(0);
    } finally {
      setIsCartLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
    fetchCart();
    fetchShippingCharge();
  }, []);

  // Close Drawer on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showDrawer &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target)
      ) {
        setShowDrawer(false);
        setEditingIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDrawer]);

  const handleSubmitAddress = async () => {
    await fetchAddresses();
    setFormData({
      name: "",
      email: "",
      line1: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      contact: "",
      landmark: "",
      addressType: "",
    });
    setShowDrawer(false);
    setEditingIndex(null);
  };

  const handleEdit = (index) => {
    const authToken = localStorage.getItem("authToken");
    const userId = localStorage.getItem("authUserId");

    // ✅ If logged-in user
    if (authToken && userId) {
      const addr = addresses[index];
      setFormData({
        id: addr.id,
        name: addr.name || "",
        email: addr.email || "",
        line1: addr.street || "",
        city: addr.city?.id || "",
        state: addr.state?.id || "",
        country: addr.country?.id || "",
        pincode: addr.zipcode || "",
        phone_number: addr.phone_number || "",
        landmark: addr.apartment_suite || "",
        addressType: addr.address_type || addr.addressType || "",
      });
    } else {
      // ✅ Guest user: get from localStorage
      const guestAddressString = localStorage.getItem("guestAddress");
      if (guestAddressString) {
        const addr = JSON.parse(guestAddressString);
        setFormData({
          name: addr.guest_name || "",
          email: addr.guest_email || "",
          line1: addr.guest_street || "",
          city: addr.guest_city_id || "",
          state: addr.guest_state_id || "",
          country: addr.guest_country_id || "",
          pincode: addr.guest_zipcode || "",
          phone_number: addr.guest_phone_number || "",
          landmark: addr.guest_apartment_suite || "",
          addressType: addr.guest_address_type || "",
        });
      }
    }

    setEditingIndex(index);
    setShowDrawer(true);
  };

  const handleDelete = async (index) => {
    const address = addresses[index];

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this address!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const authToken = localStorage.getItem("authToken");
          const userId = localStorage.getItem("authUserId");

          if (authToken && userId) {
            // Logged-in user: delete from API
            await axios.delete(
              `https://tyka.premierhostings.com/backend/api/user-addresses/${address.id}`,
              {
                headers: {
                  Authorization: `Bearer ${authToken}`,
                },
              }
            );
          } else {
            // Guest user: remove guest address from localStorage
            localStorage.removeItem("guestAddress");
          }

          await fetchAddresses(); // Refresh UI
          Swal.fire("Deleted!", "Your address has been deleted.", "success");
        } catch (err) {
          console.error("Error deleting address:", err);
          Swal.fire("Error!", "Failed to delete address.", "error");
        }
      }
    });
  };

  const placeCODOrder = async () => {
    const guestAddressString = localStorage.getItem("guestAddress");
    if (!selectedAddress && !guestAddressString) {
      Swal.fire(
        "Error!",
        "Please select an address before proceeding.",
        "error"
      );
      return;
    }

    try {
      setPlacingOrder(true);
      const userId = localStorage.getItem("authUserId");
      const authToken = localStorage.getItem("authToken");
      const guestToken = localStorage.getItem("guest_token");

      let payload = {
        payment_type: "cod",
      };

      if (userId && authToken) {
        // Logged-in user payload
        payload = {
          ...payload,
          user_id: userId,
          user_address_id: selectedAddress,
        };
      } else {
        // Guest user payload
        const guestAddressString = localStorage.getItem("guestAddress");
        if (!guestAddressString) {
          Swal.fire("Error!", "Guest address not found.", "error");
          return;
        }

        const guestAddress = JSON.parse(guestAddressString);
        const guestToken = localStorage.getItem("guest_token");

        // Build full address string
        const fullAddress = `${guestAddress.guest_street}, ${guestAddress.guest_apartment_suite}, ${guestAddress.guest_city_name}, ${guestAddress.guest_state_name}, ${guestAddress.guest_country_name}, ${guestAddress.guest_zipcode}`;
        const guestpincode = `${guestAddress.guest_zipcode}`;
        const guestcountry = `${guestAddress.guest_country_id}`;
        const gueststate = `${guestAddress.guest_state_id}`;
        const guestcity = `${guestAddress.guest_city_id}`;

        payload = {
          ...payload,
          guest_token: guestToken,
          name: guestAddress.guest_name,
          email: guestAddress.guest_email,
          phone: guestAddress.guest_phone_number,
          shipping_address: fullAddress,
          guest_pincode: guestpincode,
          guest_city: guestcity,
          guest_country: guestcountry,
          guest_state: gueststate,
        };
        console.log("shdfuiieuhrdgubefduhgdfu", payload, authToken);
      }

      console.log("shdfuiieuhrdgubefduhgdfu", payload, authToken);
      debugger;
      const res = await axios.post(
        `https://tyka.premierhostings.com/backend/api/orders?guest_token=${guestToken}`,
        payload,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      const { order_code } = res.data;
      if (res.data.success === true) {
        setCart([]);
        setTotal(0);
        setCartCount(0);

        if (userId && authToken) {
          await fetchMenuCounts(userId, authToken);
          await fetchCart();
        } else {
          await setCartCount(res.data.cart || 0);
          localStorage.clear();
        }

        Swal.fire({
          title: "Success!",
          html: `Order placed successfully with COD! <br />
               <strong>Order Code:</strong> <b>${order_code}</b>`,
          icon: "success",
        });

        navigate("/orderplaced");
      }
    } catch (err) {
      console.error("❌ Error placing order:", err.response.data.message);
      Swal.fire(
        `${err.response.data.message}` || "Error!",
        "Failed to place COD order.",
        "error"
      );
    } finally {
      setPlacingOrder(false);
    }
  };

  const handleCODClick = () => {
    const guestAddressString = localStorage.getItem("guestAddress");
    if (!selectedAddress && !guestAddressString) {
      Swal.fire(
        "Error!",
        "Please select an address before proceeding.",
        "error"
      );
      return;
    }
    setShowCODModal(true);
  };

  const handleCODConfirm = () => {
    setShowCODModal(false);
    placeCODOrder();
  };

  const handleCODCancel = () => {
    setShowCODModal(false);
  };

  const handlePayU = async () => {
    const guestAddressString = localStorage.getItem("guestAddress");
    if (!selectedAddress && !guestAddressString) {
      Swal.fire(
        "Error!",
        "Please select an address before proceeding.",
        "error"
      );
      return;
    }

    try {
      setPlacingOrder(true);
      const userId = localStorage.getItem("authUserId");
      const authToken = localStorage.getItem("authToken");
      const guestToken = localStorage.getItem("guest_token");

      let payload = {
        payment_type: "payu",
      };

      // ✅ Logged-in User
      if (userId && authToken) {
        payload = {
          ...payload,
          user_id: userId,
          session_id: userId, // or generate session id
          user_address_id: selectedAddress,
        };
      }
      // ✅ Guest User
      else if (guestToken && guestAddressString) {
        const guestAddress = JSON.parse(guestAddressString);
        const fullAddress = `${guestAddress.guest_street}, ${guestAddress.guest_apartment_suite}, ${guestAddress.guest_city_name}, ${guestAddress.guest_state_name}, ${guestAddress.guest_country_name}, ${guestAddress.guest_zipcode}`;
        const guestpincode = `${guestAddress.guest_zipcode}`;
        const guestcountry = `${guestAddress.guest_country_id}`;
        const gueststate = `${guestAddress.guest_state_id}`;
        const guestcity = `${guestAddress.guest_city_id}`;

        payload = {
          ...payload,
          guest_token: guestToken,
          name: guestAddress.guest_name,
          email: guestAddress.guest_email,
          phone: guestAddress.guest_phone_number,
          shipping_address: fullAddress,
          guest_pincode: guestpincode,
          guest_city: guestcity,
          guest_country: guestcountry,
          guest_state: gueststate,
        };

        console.log("payload", payload);
      }

      debugger;
      debugger;
      // 🔗 API call (include guest_token in query if guest)
      const res = await axios.post(
        `https://tyka.premierhostings.com/backend/api/orders${
          guestToken ? `?guest_token=${guestToken}` : ""
        }`,
        payload,
        {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        }
      );

      if (res.data?.payment) {
        setCart([]);
        setTotal(0);
        setCartCount(0);

        if (userId && authToken) {
          await fetchMenuCounts(userId, authToken);
          await fetchCart();
        } else {
          setCartCount(res.data.cart || 0);
          localStorage.clear(); // clear guest address after order
        }

        setPayUData(res.data.payment);
      } else {
        Swal.fire("Error!", "Failed to initiate PayU payment.", "error");
      }
    } catch (err) {
      console.error("❌ Error PayU order:", err);
      Swal.fire("Error!", "Failed to place PayU order.", "error");
    } finally {
      setPlacingOrder(false);
    }
  };

  if (payUData) {
    return <PayUForm payment={payUData} />;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 relative">
      {/* COD Confirmation Modal */}
      {showCODModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={handleCODCancel}
        >
          <div
            className="bg-white rounded shadow-lg p-6 w-[90%] sm:w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-2 text-center w-full uppercase bg-black text-white p-2 rounded">
              Confirm COD Order
            </h2>

            <div className="text-right font-bold text-xl mt-4">
              <div className="flex justify-between">
                <span>Total product price:</span>
                <span>₹{total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Shipping Charge:</span>
                <span>₹{shippingCharge.toFixed(2)}</span>
              </div>

              {/* Only show discount if promoDiscount is greater than 0 */}
              {promoDiscount > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Discount:</span>
                  <span>- ₹{promoDiscount.toFixed(2)}</span>
                </div>
              )}

              <hr className="my-2 border-t border-gray-300" />

              <div className="flex justify-between">
                <span>Grand Total:</span>
                <span>
                  ₹{(total + shippingCharge - promoDiscount).toFixed(2)}
                </span>
              </div>
            </div>

            <div className="flex justify-center gap-2 mt-5">
              <button
                onClick={handleCODCancel}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleCODConfirm}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rest of your component */}
      {/* 🔻 Keep your drawer, cart, addresses, and buttons as they are */}
      {/* 👇 JUST replace the COD button onClick from `handleCOD` to `handleCODClick` */}
      {/* Drawer */}
      {showDrawer && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div ref={drawerRef}>
            {editingIndex !== null ? (
              <EditAddressForm
                formData={formData}
                setFormData={setFormData}
                onClose={() => {
                  setShowDrawer(false);
                  setEditingIndex(null);
                }}
                onSubmit={handleSubmitAddress}
              />
            ) : (
              <AddressForm
                formData={formData}
                addresses={addresses}
                setFormData={setFormData}
                onClose={() => {
                  setShowDrawer(false);
                  setEditingIndex(null);
                }}
                onSubmit={handleSubmitAddress}
              />
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* LEFT SIDE */}
        {userId ? (
          <div className="bg-white shadow p-4 w-full relative h-fit rounded-md max-w-3xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
              <h2 className="font-semibold text-gray-600 text-[20px]">
                Select Delivery Address
              </h2>
              {addresses.length > 0 && (
                <button
                  onClick={() => setShowDrawer(true)}
                  className="text-blue-600 border border-blue-500 rounded px-2 py-0.5 text-sm hover:bg-blue-50 transition"
                >
                  + ADD NEW ADDRESS
                </button>
              )}
            </div>

            {addresses.length === 0 && (
              <div className="text-center py-2">
                <div className="text-black mb-2 flex justify-center">
                  <MapPin size={30} />
                </div>
                <p className="font-semibold text-[18px]">
                  No Address found in your account!
                </p>
                <p className="text-gray-500 mb-2 text-[16px]">
                  Add a delivery address
                </p>
                <button
                  onClick={() => setShowDrawer(true)}
                  className="bg-red-600 text-white px-4 py-1.5 rounded text-sm hover:bg-red-700"
                >
                  ADD ADDRESS
                </button>
              </div>
            )}

            {addresses.length > 0 &&
              addresses.map((addr, index) => (
                <div
                  key={addr.id || index}
                  className="bg-red-100 p-4 sm:p-6 rounded-lg relative mt-2"
                >
                  <div className="flex flex-col sm:flex-row items-start gap-3">
                    <input
                      type="radio"
                      name="selectedAddress"
                      checked={selectedAddress === addr.id}
                      onChange={() => setSelectedAddress(addr.id)}
                      className="mt-1 ml-1"
                    />
                    <div className="flex-1">
                      <p className="text-m text-gray-600 font-semibold">
                        {addr.address_type || addr.addressType}
                      </p>
                      <p className="font-bold text-m">{addr.name}</p>
                      {addr.email && (
                        <p className="text-m text-gray-700">
                          Email: {addr.email}
                        </p>
                      )}
                      <p className="text-m text-gray-700">
                        {addr.street}, {addr.apartment_suite},{" "}
                        {addr.city?.name || addr.city},{" "}
                        {addr.state?.name || addr.state},{" "}
                        {addr.country?.name || addr.country}, {addr.zipcode}
                      </p>
                      <p className="text-m text-gray-700">
                        Contact: {addr.phone_number || addr.contact}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 items-end text-sm">
                      <span
                        onClick={() => handleEdit(index)}
                        className="text-blue-600 cursor-pointer hover:underline"
                      >
                        EDIT
                      </span>
                      <span
                        onClick={() => handleDelete(index)}
                        className="text-red-600 cursor-pointer hover:underline"
                      >
                        DELETE
                      </span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="bg-white shadow p-4 w-full relative h-fit">
            <div className="flex justify-between items-center mb-3">
              <h2 className="font-semibold text-gray-600 text-lg sm:text-xl">
                Enter your Address
              </h2>
              <p className="text-gray-600 text-md font-semibold sm:text-lg">
                As a Guest
              </p>
            </div>

            {addresses.length === 0 && (
              <div className="text-center py-2">
                <div className="text-black mb-2 flex justify-center">
                  <MapPin size={30} />
                </div>
                <p className="font-semibold text-base sm:text-lg">
                  Add a delivery address
                </p>
                <p className="text-gray-500 mb-2 text-sm sm:text-base"></p>
                <button
                  onClick={() => setShowDrawer(true)}
                  className="bg-red-600 text-white px-4 py-1.5 rounded text-sm hover:bg-red-700"
                >
                  ADD ADDRESS
                </button>
              </div>
            )}

            {addresses.length > 0 &&
              addresses.map((addr, index) => (
                <div
                  key={addr.id || index}
                  className="bg-red-100 p-6 rounded-lg relative mt-2"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <p className="font-bold capitalize text-m">
                        Name: {addr.guest_name}
                      </p>
                      {addr.guest_email && (
                        <p className="text-m font-bold capitalize text-gray-700">
                          Email: {addr.guest_email}
                        </p>
                      )}
                      <p className="text-m text-gray-700">
                        {addr.guest_street}, {addr.guest_apartment_suite},{" "}
                        {addr.guest_city_name || addr.city},{" "}
                        {addr.guest_state_name || addr.state},{" "}
                        {addr.guest_country_name || addr.country},{" "}
                        {addr.guest_zipcode}
                      </p>
                      <p className="text-m text-gray-600 capitalize font-semibold">
                        {addr.guest_address_type || addr.addressType}
                      </p>
                      <p className="text-m text-gray-700">
                        Contact: {addr.guest_phone_number || addr.contact}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 items-end text-sm">
                      <span
                        onClick={() => handleEdit(index)}
                        className="text-blue-600 cursor-pointer hover:underline"
                      >
                        EDIT
                      </span>
                      <span
                        onClick={() => handleDelete(index)}
                        className="text-red-600 cursor-pointer hover:underline"
                      >
                        DELETE
                      </span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* RIGHT SIDE */}
        <div className="bg-white shadow p-6">
          <hr className="mb-4 border-t border-gray-200" />
          <h2 className="text-2xl font-bold mb-4">Your Order</h2>

          <div className="flex justify-between items-center border-y border-gray-300 py-3 mb-4 font-semibold text-lg">
            <span className="text-gray-900 text-[18px]">Product</span>
            <span className="text-gray-500 text-[18px]">Subtotal</span>
          </div>

          {/* ✅ Loader / Empty / Items */}
          {isCartLoading ? (
            <p className="text-gray-500">Loading cart...</p>
          ) : cart.length === 0 ? (
            <p className="text-red-500 font-semibold text-center py-4">
              🛒 No items in your cart
            </p>
          ) : (
            <>
              {cart.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4 border-b pb-4"
                >
                  <div className="flex gap-4 items-start sm:items-center">
                    <img
                      src={item.product?.default_image || "/placeholder.svg"}
                      alt={item.product?.name}
                      className="w-20 h-20 rounded object-cover flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-base font-semibold truncate w-full sm:w-44">
                        {item.product?.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        Price: ₹{item.price}
                      </p>
                      <p className="text-sm text-gray-500">
                        Qty: {item.quantity}
                      </p>
                    </div>
                  </div>
                  <p className="font-bold text-lg text-gray-700 sm:text-right">
                    ₹{item.total}
                  </p>
                </div>
              ))}
            </>
          )}

          {/* ✅ Disable buttons if cart empty */}
          {/* Example */}
          <button
            onClick={handleCODClick}
            disabled={cart.length === 0 || isCartLoading || placingOrder}
            className={`w-full mt-4 py-4 rounded font-bold text-lg flex items-center justify-center gap-2 ${
              cart.length === 0 || isCartLoading || placingOrder
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gray-800 text-white hover:bg-gray-900"
            }`}
          >
            🏠 CASH ON DELIVERY
          </button>

          <button
            onClick={handlePayU}
            disabled={cart.length === 0 || isCartLoading || placingOrder}
            className={`w-full mt-4 py-4 rounded font-bold text-lg flex items-center justify-center gap-2 ${
              cart.length === 0 || isCartLoading || placingOrder
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700"
            }`}
          >
            💰 Pay via Payu
          </button>
        </div>
      </div>

      {/* 🔺 Same logic applies to PayU button, which is untouched */}
    </div>
  );
};

export default PaymentPage;
